<template>
  <v-bottom-navigation
    bg-color="neutral0"
    height="64"
    grow
    :order="order"
  >
    <v-btn :ripple="false" to="/bookmarks" :active="false">
      <PhBookmark class="text-h5 mb-1" :weight="isBookmarkLinkActive ? 'fill' : 'regular'" />
      <span class="text-caption">{{ $t('layout.nav.bookmarks') }}</span>
    </v-btn>

    <DropdownHybrid v-model="isAddMenuOpen" :sheet-title="$t('layout.nav.add')">
      <template #activator="{ props: activatorProps }">
        <v-btn v-bind="activatorProps" :ripple="false" :active="false">
          <PhPlus class="text-h5 mb-1" weight="regular" />
          <span class="text-caption">{{ $t('layout.nav.add') }}</span>
        </v-btn>
      </template>

      <v-list bg-color="neutral25" class="py-3">
        <v-list-item @click="onAddBookmark">
          <template #prepend>
            <PhBookmarkSimple class="text-h5 mr-3" />
          </template>
          <v-list-item-title class="text-neutral900 font-weight-medium">
            {{ $t('component.add_button.add_bookmark') }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item @click="onAddNote">
          <template #prepend>
            <PhNote class="text-h5 mr-3" />
          </template>
          <v-list-item-title class="text-neutral900 font-weight-medium">
            {{ $t('component.add_button.add_note') }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item v-if="imageUploadsEnabled" @click="onAddFile">
          <template #prepend>
            <PhImage class="text-h5 mr-3" />
          </template>
          <v-list-item-title class="text-neutral900 font-weight-medium">
            {{ $t('component.add_button.add_image') }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </DropdownHybrid>

    <v-btn :ripple="false" to="/account" :active="false">
      <PhUser class="text-h5 mb-1" :weight="isAccountLinkActive ? 'fill' : 'regular'" />
      <span class="text-caption">{{ $t('layout.nav.account') }}</span>
    </v-btn>
  </v-bottom-navigation>
</template>

<script setup>
import { ref, computed, defineProps } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { PhUser, PhBookmark, PhPlus, PhBookmarkSimple, PhNote, PhImage } from '@phosphor-icons/vue'
import DropdownHybrid from '@/components/global/DropdownHybrid.vue'

// define props
const props = defineProps({
  order: {
    type: Number,
    default: 2000,
  },
})

const route = useRoute()
const router = useRouter()
const store = useStore()

// State
const isAddMenuOpen = ref(false)

const isBookmarkLinkActive = computed(() => {
  return route.path === '/bookmarks'
})

const isAccountLinkActive = computed(() => {
  return route.path === '/account'
})

const collectionId = computed(() => {
  if (route.path.startsWith('/collections/')) {
    return route.params.id
  }
  return null
})

const addBookmarkUrl = computed(() => {
  const collectionParam = collectionId.value ? `&collectionId=${collectionId.value}` : ''
  return `/bookmarks/add?referrer=bottom-nav${collectionParam}`
})

const addNoteUrl = computed(() => {
  const collectionParam = collectionId.value ? `&collectionId=${collectionId.value}` : ''
  return `/notes/add?referrer=bottom-nav${collectionParam}`
})

const onAddBookmark = () => {
  isAddMenuOpen.value = false
  router.push(addBookmarkUrl.value)
}

const onAddNote = () => {
  isAddMenuOpen.value = false
  router.push(addNoteUrl.value)
}

// runtime feature flag (admin-toggled); the API has its own kill switch
const imageUploadsEnabled = computed(() => store.getters['features/ImageUploads'])

const onAddFile = () => {
  isAddMenuOpen.value = false
  const collectionParam = collectionId.value ? `&collectionId=${collectionId.value}` : ''
  router.push(`/files/add?referrer=bottom-nav${collectionParam}`)
}
</script>

<style lang="scss" scoped>

.v-bottom-navigation {
  z-index: 3 !important;
}
</style>
